import React, { useState, useEffect, useRef } from "react";
import { adminListCars, adminCreateCar, adminUpdateCar, adminDeleteCar, uploadImage } from "../../api";
import { Plus, Pencil, Trash2, X, Upload, Link as LinkIcon, Loader2, GripVertical, Car as CarIcon, Bike, Layers } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const EMPTY = {
  title: "", price: "", status: "available", vehicleType: "car", videoUrl: "", year: new Date().getFullYear(),
  mileage: "", transmission: "Manual", fuel: "Petrol", engine: "", colour: "",
  images: [], short: "", description: "", featured: false,
};

const statusColors = {
  available: "bg-green-100 text-green-800",
  hold: "bg-yellow-100 text-yellow-800",
  sold: "bg-red-100 text-red-800",
  draft: "bg-gray-200 text-gray-600",
};

const statusLabels = { available: "Available", hold: "On Hold", sold: "Sold", draft: "Draft" };

const CarManager = () => {
  const [cars, setCars] = useState([]);
  const [vtab, setVtab] = useState("car");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftsOnly, setDraftsOnly] = useState(false);
  const fileRef = useRef(null);
  const { toast } = useToast();

  const load = () => adminListCars().then(setCars).catch(() => setCars([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, vehicleType: vtab }); setUrlInput(""); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...c, price: c.price ?? "", images: c.images || [] });
    setUrlInput("");
    setOpen(true);
  };

  const persist = async (statusOverride) => {
    const payload = {
      ...form,
      status: statusOverride || form.status,
      price: form.price === "" ? null : parseInt(form.price, 10),
      year: parseInt(form.year, 10),
      images: form.images,
    };
    delete payload.id; delete payload.slug; delete payload.priceLabel; delete payload.createdAt;
    if (editing) await adminUpdateCar(editing.id, payload);
    else await adminCreateCar(payload);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await persist();
      toast({ title: editing ? "Vehicle updated" : "Vehicle added" });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: "Save failed", variant: "destructive" });
    }
  };

  const saveDraft = async () => {
    if (!form.title) return toast({ title: "Add a title first", variant: "destructive" });
    setSavingDraft(true);
    try {
      await persist("draft");
      setForm((p) => ({ ...p, status: "draft" }));
      toast({ title: "Saved as draft", description: "This vehicle is hidden from your website." });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSavingDraft(false);
    }
  };

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (!list.length) return;
    setUploading(true);
    const uploaded = [];
    let failed = 0;
    for (const f of list) {
      try {
        const res = await uploadImage(f);
        uploaded.push(res.url);
        // add each photo as it completes so progress is visible
        setForm((prev) => ({ ...prev, images: [...prev.images, res.url] }));
      } catch (err) {
        failed += 1;
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (uploaded.length) toast({ title: `${uploaded.length} image(s) uploaded${failed ? `, ${failed} failed` : ""}` });
    else toast({ title: "Upload failed", description: "Please try again (images up to 25MB).", variant: "destructive" });
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (u) {
      setForm((prev) => ({ ...prev, images: [...prev.images, u] }));
      setUrlInput("");
    }
  };

  const removeImage = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const reorder = (from, to) => {
    if (from === null || to === null || from === to) return;
    setForm((prev) => {
      const imgs = [...prev.images];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(to, 0, moved);
      return { ...prev, images: imgs };
    });
  };

  const makeMain = (idx) => reorder(idx, 0);

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.title}?`)) return;
    await adminDeleteCar(c.id);
    toast({ title: "Car deleted" });
    load();
  };

  const field = (k, v) => setForm({ ...form, [k]: v });

  const shown = cars
    .filter((c) => (c.vehicleType || "car") === vtab)
    .filter((c) => (draftsOnly ? c.status === "draft" : true));
  const draftCount = cars.filter((c) => (c.vehicleType || "car") === vtab && c.status === "draft").length;
  const counts = {
    car: cars.filter((c) => (c.vehicleType || "car") === "car").length,
    motorcycle: cars.filter((c) => c.vehicleType === "motorcycle").length,
  };
  const vlabel = vtab === "motorcycle" ? "Motorcycle" : "Car";

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif-display text-2xl font-bold gpm-text-green">Inventory</h2>
        <button data-testid="add-vehicle-btn" onClick={openNew} className="flex items-center gap-2 px-4 py-2.5 gpm-text-cream text-sm font-semibold uppercase tracking-wide" style={{ backgroundColor: "var(--gpm-green)" }}>
          <Plus size={16} /> Add {vlabel}
        </button>
      </div>

      {/* Cars / Motorcycles tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 border-b border-black/10">
        <div className="flex gap-2">
          {[{ k: "car", l: "Cars", icon: CarIcon }, { k: "motorcycle", l: "Motorcycles", icon: Bike }].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.k} data-testid={`inventory-tab-${t.k}`} onClick={() => setVtab(t.k)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${vtab === t.k ? "gpm-text-green" : "text-gray-400 border-transparent hover:text-gray-600"}`} style={vtab === t.k ? { borderColor: "var(--gpm-gold)" } : {}}>
                <Icon size={15} /> {t.l} ({counts[t.k]})
              </button>
            );
          })}
        </div>
        <button data-testid="drafts-only-toggle" onClick={() => setDraftsOnly((v) => !v)} className={`flex items-center gap-2 px-3 py-1.5 mb-1 text-xs font-semibold uppercase tracking-wide border transition-colors ${draftsOnly ? "gpm-text-cream border-transparent" : "text-gray-500 border-black/20 bg-white hover:border-black/40"}`} style={draftsOnly ? { backgroundColor: "var(--gpm-green)" } : {}}>
          <Layers size={14} /> Drafts only{draftCount ? ` (${draftCount})` : ""}
        </button>
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="gpm-bg-green gpm-text-cream text-left">
            <tr>
              <th className="p-3">{vlabel}</th><th className="p-3">Year</th><th className="p-3">Price</th>
              <th className="p-3">Status</th><th className="p-3">Featured</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-gray-400">{draftsOnly ? `No draft ${vlabel.toLowerCase()}s in progress.` : <>No {vlabel.toLowerCase()}s yet. Click <b>Add {vlabel}</b> to create one.</>}</td></tr>
            )}
            {shown.map((c) => (
              <tr key={c.id} data-testid={`vehicle-row-${c.id}`} className="border-t border-black/10 hover:bg-gray-50">
                <td className="p-3 flex items-center gap-3">
                  <img src={c.images[0]} alt="" className="w-14 h-10 object-cover rounded" />
                  <span className="font-medium gpm-text-green">{c.title}</span>
                </td>
                <td className="p-3">{c.year}</td>
                <td className="p-3 font-semibold">{c.priceLabel}</td>
                <td className="p-3"><span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[c.status] || statusColors.available}`}>{statusLabels[c.status] || c.status}</span></td>
                <td className="p-3">{c.featured ? "Yes" : "—"}</td>
                <td className="p-3 text-right">
                  <button data-testid={`edit-vehicle-${c.id}`} onClick={() => openEdit(c)} className="p-2 text-gray-500 hover:gpm-text-green"><Pencil size={16} /></button>
                  <button data-testid={`delete-vehicle-${c.id}`} onClick={() => remove(c)} className="p-2 text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white w-full max-w-2xl my-8">
            <div className="flex justify-between items-center p-5 gpm-bg-green">
              <h3 className="font-serif-display text-xl gpm-text-cream">{editing ? `Edit ${vlabel}` : `Add ${vlabel}`}</h3>
              <button onClick={() => setOpen(false)} className="gpm-text-cream"><X size={22} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <input required placeholder="Title" value={form.title} onChange={(e) => field("title", e.target.value)} className="w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
              <div className="flex gap-2">
                {[{ v: "car", l: "Car" }, { v: "motorcycle", l: "Motorcycle" }].map((t) => (
                  <button key={t.v} type="button" onClick={() => field("vehicleType", t.v)} className={`flex-1 py-2 text-sm font-semibold uppercase tracking-wide border transition-colors ${form.vehicleType === t.v ? "gpm-text-cream border-transparent" : "gpm-text-green border-black/20 bg-white"}`} style={form.vehicleType === t.v ? { backgroundColor: "var(--gpm-green)" } : {}}>
                    {t.l}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="Price (blank = POA)" type="number" value={form.price} onChange={(e) => field("price", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
                <input required placeholder="Year" type="number" value={form.year} onChange={(e) => field("year", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
                <select data-testid="vehicle-status" value={form.status} onChange={(e) => field("status", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] bg-white">
                  <option value="available">Available</option><option value="hold">On Hold</option><option value="sold">Sold</option><option value="draft">Draft — hidden from site</option>
                </select>
                <input placeholder="Mileage" value={form.mileage} onChange={(e) => field("mileage", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
                <input placeholder="Transmission" value={form.transmission} onChange={(e) => field("transmission", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
                <input placeholder="Fuel" value={form.fuel} onChange={(e) => field("fuel", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
                <input placeholder="Engine" value={form.engine} onChange={(e) => field("engine", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
                <input placeholder="Colour" value={form.colour} onChange={(e) => field("colour", e.target.value)} className="px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
              </div>
              <input placeholder="Short summary" value={form.short} onChange={(e) => field("short", e.target.value)} className="w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
              <textarea rows={3} placeholder="Full description" value={form.description} onChange={(e) => field("description", e.target.value)} className="w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
              <div>
                <label className="text-sm font-semibold gpm-text-green block mb-1">Video Walkaround (YouTube URL)</label>
                <input placeholder="https://www.youtube.com/watch?v=..." value={form.videoUrl} onChange={(e) => field("videoUrl", e.target.value)} className="w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)]" />
                <p className="text-[11px] text-gray-400 mt-1">Paste a YouTube link and buyers will see an embedded video on the listing.</p>
              </div>

              {/* Image manager */}
              <div className="border border-black/15 p-4">
                <label className="text-sm font-semibold gpm-text-green block mb-1">Car Photos</label>
                {form.images.length > 1 && <p className="text-[11px] text-gray-400 mb-3">Drag photos to reorder — the first is the main thumbnail.</p>}
                {form.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {form.images.map((img, i) => (
                      <div
                        key={i}
                        draggable
                        onDragStart={() => setDragIndex(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); reorder(dragIndex, i); setDragIndex(null); }}
                        onDragEnd={() => setDragIndex(null)}
                        className={`relative group aspect-[3/2] cursor-move transition-opacity ${dragIndex === i ? "opacity-40" : ""}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover rounded border border-black/10 pointer-events-none" />
                        <span className="absolute top-1 left-1 bg-black/60 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><GripVertical size={12} /></span>
                        <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X size={12} />
                        </button>
                        {i === 0 ? (
                          <span className="absolute bottom-1 left-1 text-[9px] gpm-bg-green text-white px-1.5 py-0.5 rounded font-semibold">Main</span>
                        ) : (
                          <button type="button" onClick={() => makeMain(i)} className="absolute bottom-1 left-1 text-[9px] bg-black/70 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black">
                            Set main
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? "Uploading…" : "Upload photos"}
                  </button>
                  <span className="text-xs text-gray-400">or</span>
                  <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <div className="relative flex-1">
                      <LinkIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())} placeholder="Paste image URL" className="w-full pl-8 pr-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm" />
                    </div>
                    <button type="button" onClick={addUrl} className="px-3 py-2 border border-black/20 text-sm font-semibold">Add</button>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">First image is used as the main thumbnail. Large photos are optimised automatically (up to 25MB).</p>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.featured} onChange={(e) => field("featured", e.target.checked)} /> Featured on homepage
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-black/20 text-sm font-semibold">Cancel</button>
                <button type="button" data-testid="save-draft-btn" onClick={saveDraft} disabled={savingDraft} className="flex items-center gap-2 px-5 py-2.5 border-2 text-sm font-semibold disabled:opacity-60" style={{ borderColor: "var(--gpm-gold)", color: "var(--gpm-green)" }}>
                  {savingDraft ? <Loader2 size={15} className="animate-spin" /> : null} Save as draft
                </button>
                <button type="submit" data-testid="save-vehicle-btn" className="px-5 py-2.5 gpm-text-cream text-sm font-semibold" style={{ backgroundColor: "var(--gpm-green)" }}>{editing ? "Save" : "Save & publish"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarManager;
